// Columns of the charter requests table (request, route, departure, user, aircraft, prices, booking,
// status, removable ad-hoc labels). Every column declares a width: the page derives scroll.x from them.
import type { NavigateFunction } from 'react-router-dom';
import { Button, Space, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import type { CharterRequestRow, CharterRequestStatus } from '../../api/types';
import { AdhocTag } from '../../components/AdhocTag';
import { airportLabel, formatCategory } from '../../data/airportLabel';
import { gbp } from '../../data/format';
import { withAbbr } from '../../data/airportTime';
import {
  CHARTER_REQUEST_ADHOC_COLORS,
  CHARTER_REQUEST_STATUS_COLORS,
  CHARTER_REQUEST_STATUS_LABELS,
  statusLabel,
} from '../../data/statuses';

const { Text } = Typography;

export function charterRequestColumns({
  nav,
  dismissPaymentRequired,
  dismissQuoteRefresh,
}: {
  nav: NavigateFunction;
  dismissPaymentRequired: { isPending: boolean; mutate: (id: string) => void };
  dismissQuoteRefresh: { isPending: boolean; mutate: (id: string) => void };
}): ColumnsType<CharterRequestRow> {
  return [
    {
      // SHAR-801 (retest 2) — the request id stays put while the rest of
      // the table scrolls. It is the column you navigate by: without it
      // pinned, scrolling right leaves twelve columns of figures with
      // nothing saying which request they belong to.
      title: 'Request',
      key: 'id',
      width: 120,
      fixed: 'left',
      // Regression 29 Sep — CH-XXXXXXXX, as the admin emails and the
      // booking page print it, so a reference can be read across.
      render: (_, r) => <Text code style={{ whiteSpace: 'nowrap' }}>{`CH-${r.id.slice(-8).toUpperCase()}`}</Text>,
    },
    {
      // SHAR-580/584/585 — ICAO with city and country, not a bare IATA pair.
      title: 'Route',
      key: 'route',
      width: 260,
      // Regression 29 Sep - wraps inside its column, as on Charter
      // bookings (SHAR-734). nowrap ran "LFPG - Paris (Roissy-en-France,
      // Val-d'Oise), France" (361px) over the Departure column.
      render: (_, r) => (
        <div style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
          {airportLabel(r.originCode, r.originCity, r.originCountry)}
          <br />→ {airportLabel(r.destCode, r.destCity, r.destCountry)}
        </div>
      ),
    },
    {
      // SHAR-801 (retest) — this column had no width, so antd gave it
      // whatever was left and "2026-09-08 10:12:00" wrapped one character
      // per line, making the row several times taller than the others.
      title: 'Departure',
      key: 'dep',
      width: 180,
      render: (_, r) => (
        <div style={{ whiteSpace: 'nowrap' }}>{withAbbr(`${r.departureDate} ${r.departureTime}`, r.departureZoned)}</div>
      ),
    },
    { title: 'Seats', dataIndex: 'seats', key: 'seats', width: 70 },
    {
      title: 'User',
      key: 'user',
      width: 220,
      ellipsis: { showTitle: true },
      render: (_, r) => (r.user ? `${r.user.name} (${r.user.email})` : '—'),
    },
    {
      title: 'Quotes',
      dataIndex: 'quotesCount',
      key: 'q',
      width: 100,
    },
    {
      // SHAR-580 — the aircraft the user originally picked: model, tail
      // number, operator and category were all absent from this table.
      title: 'Original aircraft',
      key: 'aircraft',
      width: 180,
      // Regression 29 Sep - wraps like Route: a model name longer than
      // the column (no length limit in the API) was drawn over Category.
      render: (_, r) => (
        <div style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
          {r.modelName ?? '—'}
          {r.tailNumber ? <><br /><Text code>{r.tailNumber}</Text></> : null}
        </div>
      ),
    },
    {
      title: 'Category',
      key: 'category',
      width: 150,
      render: (_, r) => formatCategory(r.category),
    },
    {
      title: 'Operator',
      key: 'operator',
      width: 160,
      ellipsis: { showTitle: true },
      render: (_, r) => r.operatorName ?? '—',
    },
    {
      // SHAR-580 — pre-auth amount, or "Expired" in red.
      title: 'Pre-auth',
      key: 'preauth',
      width: 120,
      render: (_, r) =>
        r.preAuthExpired ? (
          <Text type="danger">Expired</Text>
        ) : r.preAuthAmountGbp != null ? (
          `${gbp(r.preAuthAmountGbp)}`
        ) : (
          '—'
        ),
    },
    {
      // SHAR-580 — the price the user was shown when they asked.
      title: 'Indicative',
      key: 'indicative',
      width: 120,
      render: (_, r) =>
        r.indicativePriceGbp != null ? `${gbp(r.indicativePriceGbp)}` : '—',
    },
    {
      // SHAR-580 — what the request actually sold for, which is the
      // price of the quote the user booked. Empty until one is booked.
      title: 'Sold price',
      key: 'sold',
      width: 120,
      render: (_, r) =>
        r.soldPriceGbp != null ? `${gbp(r.soldPriceGbp)}` : '—',
    },
    {
      // SHAR-117 — a booked request links to the booking it produced.
      // The click is stopped from reaching the row so the link goes to
      // the booking rather than opening the request underneath it.
      title: 'Booking',
      key: 'booking',
      width: 130,
      render: (_, r) =>
        r.bookingId ? (
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              nav(`/charter-bookings/${r.bookingId}`);
            }}
          >
            {r.bookingReference ?? r.bookingId.slice(-8)}
          </Button>
        ) : (
          '—'
        ),
    },
    {
      // SHAR-801 (retest) — the other column that declared no width.
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (s: CharterRequestStatus, r) => (
        <Tag color={CHARTER_REQUEST_STATUS_COLORS[s]}>{statusLabel(CHARTER_REQUEST_STATUS_LABELS, s, r.statusLabel)}</Tag>
      ),
    },
    {
      // SHAR-1105 — "Payment required" (SHAR-1092) in its own column, as on
      // the Bookings and Charter bookings lists, removable with a confirm.
      // Removing it hides it here only; the customer still sees it.
      title: 'Ad-hoc status',
      key: 'adhoc',
      width: 190,
      onCell: () => ({
        onClick: (e) => e.stopPropagation(),
        style: { cursor: 'default' },
      }),
      render: (_, r) =>
        r.adhocPaymentRequired || r.adhocQuoteRefresh ? (
          <Space direction="vertical" size={4}>
            {r.adhocQuoteRefresh ? (
              <AdhocTag
                color={CHARTER_REQUEST_ADHOC_COLORS.quoteRefresh}
                label="Quote refresh"
                title="Remove label"
                description="Are you sure you want to remove the Quote refresh status?"
                loading={dismissQuoteRefresh.isPending}
                onRemove={() => dismissQuoteRefresh.mutate(r.id)}
              />
            ) : null}
            {r.adhocPaymentRequired ? (
              <AdhocTag
                color={CHARTER_REQUEST_ADHOC_COLORS.paymentRequired}
                label="Payment required"
                title="Remove label"
                description="Remove the “Payment required” label from this charter request? It is removed in the admin panel only; the customer still sees it."
                loading={dismissPaymentRequired.isPending}
                onRemove={() => dismissPaymentRequired.mutate(r.id)}
              />
            ) : null}
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];
}
